/**
 * Single source of truth for plan-feature matrix.
 *
 * Each row: [label, { free, starter, pro, agency }]
 *   - true  → feature unlocked
 *   - false → feature locked
 *   - "<text>" → renders as text (limit / numeric value)
 *
 * Read by:
 *   - components/app/ComparisonMatrix.jsx (full /pricing table)
 *   - components/app/PlanLockedCard.jsx (dashboard "what's locked" CTA)
 *
 * Mirror this shape with backend services/billing.py::PLAN_FEATURES when you
 * change a row that maps to a real gate.
 */

export const TIERS = [
  { id: "free",    label: "Free trial", price: "$0",  sub: "7 days", popular: false },
  { id: "starter", label: "Starter",    price: "$19", sub: "/mo",    popular: false },
  { id: "pro",     label: "Pro",        price: "$39", sub: "/mo",    popular: true  },
  { id: "agency",  label: "Agency",     price: "$99", sub: "/mo",    popular: false },
];


export const FEATURE_GROUPS = [
  {
    name: "Projects & sites",
    rows: [
      ["Projects",                          { free: "1",     starter: "1",     pro: "5",         agency: "25"        }],
      ["Audits per month",                  { free: "50",    starter: "100",   pro: "Unlimited", agency: "Unlimited" }],
      ["Audit history (days)",              { free: "30",    starter: "180",   pro: "365",       agency: "365"       }],
    ],
  },
  {
    name: "Dashboard & UX",
    rows: [
      ["Simple Mode (SMB dashboard)",       { free: true,    starter: true,    pro: true,        agency: true  }],
      ["Pro dashboard (full pipeline)",     { free: true,    starter: false,   pro: true,        agency: true  }],
      ["Score projection & next-actions",   { free: true,    starter: true,    pro: true,        agency: true  }],
      ["Weekly digest email",               { free: false,   starter: true,    pro: true,        agency: true  }],
      ["Milestone toasts & streak",         { free: true,    starter: true,    pro: true,        agency: true  }],
    ],
  },
  {
    name: "AI features",
    rows: [
      ["Inline AI fixes (meta, title, H1, alt)", { free: false, starter: true,  pro: true,       agency: true  }],
      ["AI Content Studio",                 { free: false,   starter: false,   pro: true,        agency: true  }],
      ["AI section generation",             { free: false,   starter: true,    pro: true,        agency: true  }],
      ["AI recommendations / action plan",  { free: false,   starter: true,    pro: true,        agency: true  }],
      ["Content Gap (SERP analysis)",       { free: false,   starter: false,   pro: true,        agency: true  }],
      ["Magic Mode (keyword → draft)",      { free: false,   starter: true,    pro: true,        agency: true  }],
    ],
  },
  {
    name: "Publishing & integrations",
    rows: [
      ["WordPress one-click publish",       { free: false,   starter: true,    pro: true,        agency: true  }],
      ["Notion publish",                    { free: false,   starter: false,   pro: true,        agency: true  }],
      ["Social platforms (drafts)",         { free: "1",     starter: "3",     pro: "6",         agency: "6"   }],
      ["Variants per post",                 { free: "1",     starter: "3",     pro: "5",         agency: "5"   }],
      ["Scheduled posts per month",         { free: "5",     starter: "30",    pro: "100",       agency: "Unlimited" }],
    ],
  },
  {
    name: "Support & extras",
    rows: [
      ["Email support",                     { free: false,   starter: true,    pro: true,        agency: true  }],
      ["Priority generation queue",         { free: false,   starter: false,   pro: false,       agency: true  }],
      ["Priority support",                  { free: false,   starter: false,   pro: false,       agency: true  }],
      ["Early access to new platforms",     { free: false,   starter: false,   pro: false,       agency: true  }],
    ],
  },
];


/**
 * Return the locked features for a given plan id, plus what the next tier would
 * unlock. Used by PlanLockedCard.
 *
 * `locked` is the union of every row where this plan === false OR this plan's
 * numeric/text value is strictly less than the next tier's.
 *
 * Returns: { plan, nextPlan, locked: [{ label, currentValue, unlockedAt }] }
 */
const PLAN_ORDER = ["free", "starter", "pro", "agency"];

export function computeLockedFeatures(planId) {
  const idx = PLAN_ORDER.indexOf(planId);
  if (idx === -1) return { plan: planId, nextPlan: null, locked: [] };
  // Agency = top tier, nothing locked.
  if (idx >= PLAN_ORDER.length - 1) return { plan: planId, nextPlan: null, locked: [] };

  const nextPlan = PLAN_ORDER[idx + 1];
  const locked = [];

  for (const group of FEATURE_GROUPS) {
    for (const [label, values] of group.rows) {
      const cur = values[planId];
      const next = values[nextPlan];

      // Boolean — locked if false and next tier has it.
      if (cur === false && next === true) {
        locked.push({ label, group: group.name, currentValue: "Locked", unlockedAt: nextPlan });
        continue;
      }

      // Text/number difference — locked if next tier has a "better" string.
      if (typeof cur === "string" && typeof next === "string" && cur !== next) {
        locked.push({ label, group: group.name, currentValue: cur, unlockedAt: nextPlan, nextValue: next });
      }
    }
  }

  return { plan: planId, nextPlan, locked };
}


export const PLAN_LABEL = {
  free:    "Free trial",
  starter: "Starter",
  pro:     "Pro",
  agency:  "Agency",
};

export const PLAN_PRICE = {
  free:    "$0",
  starter: "$19/mo",
  pro:     "$39/mo",
  agency:  "$99/mo",
};
